import request from 'superagent';
import { find } from 'lodash';
import { parse, stringify } from 'qs';
import moment from 'moment';
import url from '../../utils/url';
import * as actionTypes from '../actionTypes/advancedSearchActionTypes';
import dateFormat from '../../utils/dateFormat';
import { notify } from '../../components/common/Notification';
import * as docketActions from './docketActions';
import * as docketActionTypes from '../actionTypes/docketsActionTypes';

export function parseQueryStringToObject(searchParams) {
  const { business_only, ...obj } = parse(searchParams, { ignoreQueryPrefix: true });
  return {
    ...obj,
    ...(obj.page && { page: JSON.parse(obj.page) || 0 }),
    ...(obj.grouped && { grouped: !!JSON.parse(obj.grouped) }),
    ...(obj.do_search && { do_search: !!JSON.parse(obj.do_search) }),
    // handle old legacy params
    ...(business_only && {
      // eslint-disable-next-line no-nested-ternary
      business_cases: ['0', '1'].includes(business_only)
        ? JSON.parse(business_only)
          ? 'limit'
          : 'include'
        : business_only,
    }),
    ...(obj.search_pdfs && {
      search_pdfs: ['include', 'limit', 'exclude'].includes(obj.search_pdfs) ? obj.search_pdfs : 'include',
    }),
    ...(obj.case_titles && {
      case_titles: ['include', 'limit', 'exclude'].includes(obj.case_titles) ? obj.case_titles : 'include',
    }),
    ...(obj.party_name && {
      party_name: ['include', 'limit', 'exclude'].includes(obj.party_name) ? obj.party_name : 'include',
    }),
  };
}

const stringifyParams = payload => {
  const {
    // not sent params
    tab,
    // remaining params
    court_type,
    ...values
  } = payload;

  const params = {
    ...values,
    court_type: court_type === 'all' ? '' : court_type,
  };

  function filterQueryString(prefix, value) {
    // format Dates
    if ((prefix.includes('date_') || prefix.includes('open_cases_')) && value) {
      return moment.isMoment(value) ? dateFormat(value) : value;
    }

    return value;
  }

  return stringify(params, {
    arrayFormat: 'brackets',
    encodeValuesOnly: true,
    skipNulls: true,
    filter: filterQueryString,
  });
};

const updateURLSearchParams = payload => dispatch => {
  dispatch({
    type: actionTypes.ADVANCED_SEARCH_URL_PARAMS_UPDATE,
    payload,
  });
};

const getPartsPurchaseInfo = params => (dispatch, getState) => {
  function onSuccess(response) {
    const { data } = getState()
      .get('advancedSearch')
      .toJS();

    const updatedAdvancedSearchResultsData = data.results.map(docket => {
      let updatedDocket = docket;

      if (params.case_docket_sequence_numbers[0].sequence_numbers.includes(docket.docket_seq)) {
        updatedDocket = { ...docket, colorCode: 'in-pacer' };

        const { docket_seq } = docket;
        const responseEntry = find(response.body, { sequence_number: docket_seq });

        if (responseEntry) {
          if (responseEntry.has_user_purchased_any_document) {
            updatedDocket = { ...docket, colorCode: 'purchased' };
          } else {
            updatedDocket = { ...docket, colorCode: 'in-inforuptcy' };
          }
        }
      }
      return updatedDocket;
    });

    dispatch({
      type: actionTypes.ADVANCED_SEARCH_ALL_GENERAL.SUCCESS,
      data: { ...data, results: updatedAdvancedSearchResultsData },
    });

    return Promise.resolve(response.body);
  }

  return request
    .post('/rpc/dockets/purchase_info')
    .set('Accept', 'application/json')
    .send(params)
    .then(onSuccess)
    .catch(error => {
      dispatch({
        type: actionTypes.ADVANCED_SEARCH_ALL_GENERAL.ERROR,
        error,
      });
      notify.error(`Failed to update Docket's purchase status`);
    });
};

export const searchAdvancedFilings = payload => (dispatch, getState) => {
  const filterParams = { ...payload, do_search: true };
  const params = stringifyParams(filterParams);

  dispatch({
    type: actionTypes.ADVANCED_SEARCH_ALL_GENERAL.REQUEST,
    payload: filterParams,
  });

  dispatch({
    type: docketActionTypes.UPDATE_SELECTED_DOCKET,
    selectedDocket: {
      status: 'done',
      docket_no: null,
      docket_seq: null,
      binder: {},
    },
  });

  // Reset Dockets store
  dispatch({
    type: docketActionTypes.UPDATE_DOCKETS,
    entries: { status: '', data: [] },
  });

  const onSuccess = response => {
    dispatch({
      type: actionTypes.ADVANCED_SEARCH_ALL_GENERAL.SUCCESS,
      data: response || {},
    });

    dispatch(updateURLSearchParams(params));

    if (response.results && response.results.length > 0) {
      const seqNumbers = response.results.reduce((accum, next) => {
        if (next.docket_seq) {
          accum.push(next.docket_seq);
        }
        return accum;
      }, []);

      if (seqNumbers.length > 0) {
        const purchaseInfoParams = {
          case_docket_sequence_numbers: [
            {
              court_code: response.results[0].case_court,
              case_number: response.results[0].case_no,
              sequence_numbers: seqNumbers,
            },
          ],
        };

        getPartsPurchaseInfo(purchaseInfoParams)(dispatch, getState);
      }
    }
  };

  const onError = error => {
    dispatch({
      type: actionTypes.ADVANCED_SEARCH_ALL_GENERAL.ERROR,
      error,
      payload,
    });
    // TODO: add 'When contacting support, please quote Request ID:' + error.request_id
    notify.error(`Failed to return Advanced Search results`);
  };

  return request
    .get(url('/rpc/capi/cases/pacer/search-filings'))
    .set('Accept', 'application/json')
    .query(params)
    .then(({ body }) => (body && !body.error ? onSuccess(body) : onError(body && body.error)))
    .catch(error => onError(error));
};

export const resetAdvancedSearch = () => dispatch => {
  dispatch(updateURLSearchParams(''));

  dispatch({
    type: actionTypes.ADVANCED_SEARCH_RESET,
  });
};

export const getDocketDocuments = payload => (dispatch, getState) => {
  const { docket, id, ...params } = payload;

  const entries = getState()
    .getIn(['dockets', 'entries'])
    .toJS();

  // Save previous Docket in AdvSearch store
  if (entries && entries.data && entries.data.length === 1 && entries.data[0].id) {
    dispatch({
      type: actionTypes.ADVANCED_SEARCH_RESULT_DOCKET_UPDATE,
      payload: { docket: { ...entries.data[0], isExpanded: false } },
    });
  }

  // Set current Docket in store
  dispatch({
    type: docketActionTypes.UPDATE_DOCKETS,
    entries: { status: 'done', data: [{ ...docket, id }] },
  });

  dispatch(docketActions.getDocuments(params));
};

export const getPetitionPdf = params => dispatch => {
  const { case_no, court_code } = params;

  dispatch({
    type: actionTypes.ADVANCED_SEARCH_DOCUMENT_FETCH.REQUEST,
  });

  function onSuccess(body) {
    const { document } = body;

    dispatch({
      type: actionTypes.ADVANCED_SEARCH_DOCUMENT_FETCH.SUCCESS,
    });

    if (document.preview_url) {
      window.open(document.preview_url);
    } else {
      notify.error('Failed to open Document');
    }
  }

  const onError = error => {
    dispatch({
      type: actionTypes.ADVANCED_SEARCH_DOCUMENT_FETCH.ERROR,
      error,
    });

    notify.error('Failed to fetch Document');
  };

  return request
    .get(`/rpc/capi/cases/pacer/${court_code}/${case_no}/dockets/1/documents/1`)
    .set('Accept', 'application/json')
    .then(({ body }) => (body && !body.error ? onSuccess(body) : onError(body && body.error)))
    .catch(error => onError(error));
};

export const setFormFilter = payload => (dispatch, getState) => {
  dispatch({
    type: actionTypes.ADVANCED_SEARCH_FORM_FILTER_SET,
    payload,
  });

  const state = getState()
    .get('advancedSearch')
    .toJS();

  if (payload.queryParams && payload.queryParams.do_search && !state.showResults) {
    dispatch(searchAdvancedFilings(state.filter));
  }
};

export const createAlert = (formAlert, advancedSearchFilter) => dispatch => {
  dispatch({
    type: actionTypes.ADVANCED_SEARCH_ALERT.REQUEST,
    data: formAlert,
  });

  const { do_search, grouped, page, ...limitedAdvancedSearchFilter } = advancedSearchFilter;

  const queryParams = stringifyParams(limitedAdvancedSearchFilter);

  const onSuccess = alert => {
    dispatch({
      type: actionTypes.ADVANCED_SEARCH_ALERT.SUCCESS,
      data: alert,
    });
    notify.success(`Alert ${alert.name} created successfully`);
  };

  const onError = error => {
    dispatch({
      type: actionTypes.ADVANCED_SEARCH_ALERT.ERROR,
      error,
    });

    notify.error(`Failed to create alert ${formAlert.name}`);
  };

  const alertType =
    !advancedSearchFilter.template || ['general', ''].includes(advancedSearchFilter.template)
      ? 'premium'
      : advancedSearchFilter.template;

  request
    .post(url(`/search-alerts/json/add/${alertType}`))
    .send({ ...formAlert, query: queryParams })
    .then(({ body }) => {
      onSuccess(body.alert);
    })
    .catch(error => {
      onError(error);
    });
};

export const editAlert = (alert, advancedSearchFilter) => dispatch => {
  dispatch({
    type: actionTypes.ADVANCED_SEARCH_ALERT.REQUEST,
    data: alert,
  });

  const { do_search, grouped, page, ...limitedAdvancedSearchFilter } = advancedSearchFilter;

  const queryParams = stringifyParams(limitedAdvancedSearchFilter);

  const onSuccess = () => {
    dispatch({
      type: actionTypes.ADVANCED_SEARCH_ALERT.SUCCESS,
      data: alert,
    });
    notify.success(`Alert ${alert.name} edited successfully`);
  };

  const onError = error => {
    dispatch({
      type: actionTypes.ADVANCED_SEARCH_ALERT.ERROR,
      error,
    });
    notify.error(`Failed to edit alert ${alert.name}`);
  };

  request
    .post(url(`/search-alerts/json/edit/${alert.id}`))
    .set('X-Csrf-Token', alert.edit_token)
    .send({ ...alert, query: queryParams })
    .then(() => {
      onSuccess();
    })
    .catch(error => {
      onError(error);
    });
};

export const getAlert = alertId => dispatch => {
  dispatch({ type: actionTypes.ADVANCED_SEARCH_ALERT.REQUEST });

  const onSuccess = alert => {
    dispatch({
      type: actionTypes.ADVANCED_SEARCH_ALERT.SUCCESS,
      data: alert,
    });
  };

  const onError = error => {
    dispatch({
      type: actionTypes.ADVANCED_SEARCH_ALERT.ERROR,
      error,
    });

    notify.error('Failed to retrieve alert');
  };

  request
    .get(url(`/search-alerts/json/get/${alertId}`))
    .then(({ body }) => onSuccess(body))
    .catch(error => onError(error));
};



// WEBPACK FOOTER //
// ./src/redux/actions/advancedSearchAction.js